import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Typography, CircularProgress, Box } from '@material-ui/core';
import BigButtonNext from 'pages/reservationBookingOnlineV2/components/BigButtonNext';
import DesiredStoreOptions from 'pages/reservationBookingOnlineV2/components/DesiredStoreOptions';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { stringify } from 'qs';
import moment from 'moment';
import TableCalender from 'pages/reservationBookingOnlineV2/components/TableCalender';
import SectionHeader from 'pages/reservationBookingOnlineV2/components/SectionHeader';
import { StatusBooking } from 'pages/reservationBookingOnlineV2/context';
import {
  ICalenderBooking, IDateBooking, IDateCalender, IStoreSettingDetail,
} from 'pages/reservationBookingOnlineV2/pages/StepDateAndTimeVisitSelection/types';
import { getChangeBookingCalendar, getReservationChangeInfo } from 'api/reservationChangeApi';
import { getStoreSettingDetailApi } from 'api/onlineBookingV2Api';
import { NeighborhoodStores } from 'pages/reservationBookingOnlineV2/pages/StepsBeforeReservation/types';

const DAY_JA = ['日', '月', '火', '水', '木', '金', '土'];

const getIconByStatus = (status: keyof typeof StatusBooking): string => {
  switch (status) {
    case StatusBooking.AVAILABLE_BOOKING: return '◎';
    case StatusBooking.CALL_BEFORE_BOOKING: return '△';
    default: return '';
  }
};

const getClassByStatus = (status: keyof typeof StatusBooking): number => {
  switch (status) {
    case StatusBooking.AVAILABLE_BOOKING: return 0;
    case StatusBooking.CALL_BEFORE_BOOKING: return 1;
    default: return 2;
  }
};

interface WorkingCalendar {
  time: string;
  status: keyof typeof StatusBooking;
}

interface ReservationForOneDay {
  targetDate: string;
  workingCalendars: WorkingCalendar[];
}

function buildGridData(calendarData: ReservationForOneDay[]): {
  datesCalender: IDateCalender[];
  timesCalender: ICalenderBooking[][];
} {
  if (!calendarData.length) {
    return { datesCalender: [], timesCalender: [] };
  }

  const datesCalender: IDateCalender[] = calendarData.map((day) => {
    const d = moment(day.targetDate);
    const dateStr = d.format('YYYY-MM-DD');
    return {
      dayOfWeek: `${d.format('M/DD')} (${DAY_JA[d.day()]})`,
      date: dateStr,
      class: d.day(),
    };
  });

  const allTimes = Array.from(
    new Set(calendarData.flatMap((day) => day.workingCalendars.map((w) => w.time))),
  ).sort();

  const timesCalender: ICalenderBooking[][] = allTimes.map((time) => calendarData.map((day): ICalenderBooking => {
    const dateStr = moment(day.targetDate).format('YYYY-MM-DD');
    const slot = day.workingCalendars.find((w) => w.time === time);
    const status: keyof typeof StatusBooking = slot ? slot.status : 'CAN_NOT_BOOKING';
    const minutes = time.split(':')[1];
    const timeShort = minutes !== '00' ? minutes : time;
    return {
      key: `${dateStr}_${time}`,
      time,
      timeShort,
      date: dateStr,
      status,
      icon: getIconByStatus(status),
      class: getClassByStatus(status),
    };
  }));

  return { datesCalender, timesCalender };
}


const useStyles = makeStyles((theme: Theme) => createStyles({
  pageRoot: {
    width: '100%',
    minHeight: '100vh',
    backgroundColor: '#fff',
    fontFamily: 'Noto Sans JP',
    fontStyle: 'normal',
  },
  contentWrapper: {
    maxWidth: 980,
    marginTop: 30,
    marginBottom: 45,
    width: '95%',
    marginLeft: 'auto',
    marginRight: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginTop: 8,
      marginBottom: 8,
    },
  },
  calendarInstruction: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: '16px 0 8px',
    letterSpacing: '0.5px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px',
      margin: '12px 0 6px',
    },
  },
  periodNote: {
    fontSize: '11px',
    color: '#555',
    padding: '0 10px 8px',
    lineHeight: 1.6,
  },
  neighborSection: {
    marginBottom: '30px',
  },
  boxBelt: {
    marginBottom: '0.6em',
    padding: '0.1em 0.6em',
    backgroundColor: '#F4F5F5',
    borderLeft: 'solid 10px #CD0021',
    color: '#000',
    fontSize: '26px',
    lineHeight: '39px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '17px',
      lineHeight: '25px',
      letterSpacing: '0.28px',
    },
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    textDecoration: 'underline',
    textAlign: 'center' as const,
    display: 'block',
    marginTop: theme.spacing(1.5),
    fontSize: '0.9rem',
  },
}));

function DateChangePage() {
  const classes = useStyles();
  const location = useLocation<{ neighborhoodStoresList?: NeighborhoodStores[] }>();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token') || '';
  const companyCode = urlParams.get('companyCode') || '';
  const orgName = urlParams.get('orgName') || '';
  const orgId = urlParams.get('orgId') || '';

  const [weekStart, setWeekStart] = useState<moment.Moment>(() => moment().startOf('isoWeek'));
  const [selectedOrgId, setSelectedOrgId] = useState<string>(orgId);
  const [selectedOrgName, setSelectedOrgName] = useState<string>(orgName);
  const [selectedTime, setSelectedTime] = useState<ICalenderBooking | null>(null);
  const [calendarData, setCalendarData] = useState<ReservationForOneDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [neighborStores] = useState<NeighborhoodStores[]>(
    location.state?.neighborhoodStoresList || [],
  );
  const [acceptRequestReservation, setAcceptRequestReservation] = useState<boolean>(false);
  const [storeSetting, setStoreSetting] = useState<IStoreSettingDetail>();

  // 予約可能期間は選択中の店舗の設定を参照する（予約変更は既存顧客のフロー）
  useEffect(() => {
    if (!selectedOrgId || !companyCode) return;
    getStoreSettingDetailApi({ orgId: selectedOrgId, companyCode })
      .then((res) => setStoreSetting(res))
      .catch(() => setStoreSetting(undefined));
  }, [selectedOrgId, companyCode]);

  useEffect(() => {
    if (!token || !companyCode) return;
    getReservationChangeInfo(token, companyCode)
      .then((res) => {
        setAcceptRequestReservation(!!res.data?.acceptRequestReservation);
      })
      .catch(() => {
        setAcceptRequestReservation(false);
      });
  }, [token, companyCode]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    const startDate = weekStart.format('YYYY-MM-DD');
    const endDate = weekStart.clone().add(6, 'days').format('YYYY-MM-DD');
    const neighborOrgId = selectedOrgId !== orgId ? selectedOrgId : undefined;
    setLoading(true);
    getChangeBookingCalendar(token, companyCode, startDate, endDate, neighborOrgId)
      .then((res) => {
        setCalendarData(res.data || []);
      })
      .catch(() => {
        enqueueSnackbar('予約枠の取得に失敗しました。', { variant: 'error' });
        setCalendarData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token, companyCode, weekStart, selectedOrgId]);

  const { datesCalender, timesCalender } = useMemo(() => buildGridData(calendarData), [calendarData]);

  const dateBooking: IDateBooking = {
    startDate: weekStart.format('YYYY-MM-DD'),
    endDate: weekStart.clone().add(6, 'days').format('YYYY-MM-DD'),
  };

  const handleChangePrevWeek = () => {
    setWeekStart((prev) => moment(prev).subtract(7, 'days'));
    setSelectedTime(null);
  };

  const handleChangeNextWeek = () => {
    setWeekStart((prev) => moment(prev).add(7, 'days'));
    setSelectedTime(null);
  };

  const handleChangeSelectTime = (item: ICalenderBooking) => {
    setSelectedTime(item);
  };

  const handleChangeSelectDate = (date: string) => {
    setWeekStart(moment(date).startOf('isoWeek'));
    setSelectedTime(null);
  };

  const handleSelectNeighborStore = (id: string, name: string) => {
    setSelectedOrgId(id || orgId);
    setSelectedOrgName(name || orgName);
    setSelectedTime(null);
  };

  const handleSubmit = () => {
    if (!selectedTime) {
      enqueueSnackbar('日時を選択してください', { variant: 'warning' });
      return;
    }
    const selectedDatetime = `${selectedTime.date}T${selectedTime.time}:00`;
    history.push(
      `/contact/reservationChangeConfirm?${stringify({ token, companyCode })}`,
      { selectedDatetime },
    );
  };

  const handleBack = () => {
    history.replace(`/contact/reservationChange?${stringify({ token, companyCode })}`);
  };

  const periodDate = moment().add(2, 'days');
  const periodNote = `※${periodDate.format('YYYY年MM月DD日')}（${DAY_JA[periodDate.day()]}）以降でお申し込みいただけます。当日のご予約は、お電話にてお願い致します。`;

  return (
    <div className={classes.pageRoot}>
      <div className={classes.contentWrapper}>
        <SectionHeader title={acceptRequestReservation ? '2. ご来店希望日時を選択' : '2. ご来店日時を選択'} />

        {!acceptRequestReservation && (
          <p className={classes.calendarInstruction}>ご予約日時を選択してください</p>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <TableCalender
            storeSetting={storeSetting}
            storeName={selectedOrgName}
            timesCalender={timesCalender}
            datesCalender={datesCalender}
            dateBooking={dateBooking}
            handleChangePrevWeek={handleChangePrevWeek}
            handleChangeNextWeek={handleChangeNextWeek}
            handleChangeSelectTime={handleChangeSelectTime}
            handleChangeSelectDate={handleChangeSelectDate}
            disableNonBookableSlot={true}
          />
        )}

        <Typography className={classes.periodNote}>
          {periodNote}
        </Typography>

        {neighborStores.length > 0 && (
          <div className={classes.neighborSection}>
            <h4 className={classes.boxBelt}>近隣のサロンの予約状況を見る</h4>
            <DesiredStoreOptions
              list={neighborStores.map((s) => ({ id: s.orgId, text: s.orgName }))}
              defaultOption={selectedOrgId}
              handleClick={handleSelectNeighborStore}
            />
          </div>
        )}

        <div className={classes.buttonContainer}>
          <BigButtonNext content="予約を変更する" handleSubmit={handleSubmit} isDisabled={!selectedTime} />
          <Typography
            component="span"
            className={classes.backLink}
            onClick={handleBack}
          >
            戻る
          </Typography>
        </div>
      </div>
    </div>
  );
}

export default DateChangePage;
